/**
 * Sends Google Tag Manager events. Keeps track of loading google tag manager only once if required and only send
 * events if Google Tag Manager is loaded and we are able to track them or not.
 */
export default class Tracking {
    /**
     * Constructor
     * @param {object} global Global object (window in browser)
     */
    constructor(global) {
        this.global = global;
        // initialize the Google Tag Manager dataLayer if we have not done so.
        if (undefined === global.dataLayer) {
            global.dataLayer = [];
            global.dataLayer.push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
            });
        }
    }

    /**
     * Loads google tag manager script asynchronously. Because we already have a dataLayer, we can already send
     * events before the script has finished loading.
     *
     * If this method is never being called, none of the events will actually be triggered at all.
     *
     * @param  {string} gtmId
     */
    loadGoogleTagManager(gtmId) {
        if (this.alreadyBeingLoaded === gtmId || !this.global.document) {
            return;
        }
        if (this.alreadyBeingLoaded) {
            throw new TypeError('I try to load two instances of Google Tag Manager!');
        }
        this.alreadyBeingLoaded = gtmId;
        // we assume there is at least one script tag already being loaded or we just append it to <head>.
        let firstElement = this.global.document.getElementsByTagName('script')[0] || this.global.document.head;

        let scriptElement = this.global.document.createElement('script');

        scriptElement.async = true;
        scriptElement.src = '//www.googletagmanager.com/gtm.js?id=' + gtmId;
        firstElement.parentNode.insertBefore(scriptElement, firstElement);
    }

    /**
     * Returns true if the customer tells us we are allowed to track him.
     * @return {boolean}
     */
    isTrackingAllowed() {
        var tracking;

        if (this.global.navigator && this.global.navigator.doNotTrack) {
            return false;
        }

        if (!this.global.localStorage) {
            return true;
        }
        tracking = this.global.localStorage.getItem('tracking');

        return !tracking || 'true' === tracking;
    }

    /**
     * Enables tracking events.
     */
    enableTracking() {
        if (this.global.localStorage) {
            this.global.localStorage.setItem('tracking', 'true');
        }
    }

    /**
     * Disables tracking events.
     */
    disableTracking() {
        if (this.global.localStorage) {
            this.global.localStorage.setItem('tracking', 'false');
        }
    }

    /**
     * Sends one or more events to Google Tag Manager.
     * @param  {array} events
     */
    sendEvent(...events) {
        // some blockers remove the dataLayer so we need to verify this existence of the dataLayer public
        if (undefined !== this.global.dataLayer) {
            this.global.dataLayer.push(...events);
        }
    }

    /**
     * Sends one or more events to Google Tag Manager, but only if we are allowed to track.
     * @param  {array} events
     */
    sendTrackedEvent(...events) {
        if (this.isTrackingAllowed()) {
            this.sendEvent(...events);
        }
    }
}
